import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { ArrowLeft, Upload, Save, Image, Folder, X, Sparkles, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useS3Upload from '../hooks/useS3Upload';
import S3AssetBrowser from '../components/S3AssetBrowser';

const SliderImageForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For editing existing slider
  const location = useLocation();
  const { token } = useAuth();
  const { uploadFile, uploading, uploadProgress } = useS3Upload();
  
  const [saving, setSaving] = useState(false);
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [error, setError] = useState('');
  const [showAssetBrowser, setShowAssetBrowser] = useState(false);
  
  // AI Image Generation states
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [aiImageInput, setAIImageInput] = useState({ name: '', description: '' });
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImageError, setAIImageError] = useState('');
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState('');
  
  // Image preview modal state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    order: 0,
    image: null,
    imageUrl: '', // S3 URL
    s3Key: '' // S3 key for deletion
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const isEditing = !!id;

  // Load existing slider data if editing
  useEffect(() => {
    if (isEditing) {
      fetchSliderData();
    }
  }, [id]);

  // Fetch available AI image models on mount
  useEffect(() => {
    fetchAvailableImageModels();
  }, [token]);

  const fetchAvailableImageModels = async () => {
    try {
      const openaiResponse = await fetch(`${API_BASE_URL}/api/openai-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (openaiResponse.ok) {
        const openaiData = await openaiResponse.json();
        if (openaiData.success && openaiData.settings && openaiData.settings.use_openai_for_all_services) {
          // Use all available image models (OpenAI DALL-E and Google Nano Banana)
          const imageModelsData = (openaiData.image_models || []).map(model => ({
            id: model.value || model.id,
            name: model.label || model.name
          }));
          
          setAvailableImageModels(imageModelsData);

          // Set default model from settings
          const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
          setSelectedImageModel(defaultImageModel);

          console.log('🖼️ Loaded image models:', imageModelsData);
          console.log('🎯 Default image model:', defaultImageModel);
        }
      }
    } catch (error) {
      console.error('Error fetching image models:', error);
      // Set default fallback
      setAvailableImageModels([
        { id: 'dall-e-3', name: 'DALL-E 3' }
      ]);
      setSelectedImageModel('dall-e-3');
    }
  };

  const fetchSliderData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/slider-images/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title || '',
          description: data.description || '',
          buttonText: data.buttonText || '',
          buttonLink: data.buttonLink || '',
          order: data.order || 0,
          image: null, // Don't set existing image in file input
          imageUrl: data.image_url || '', // Set existing image URL for preview
          s3Key: '' // S3 key not available from backend response
        });
      }
    } catch (error) {
      console.error('Error fetching slider data:', error);
      setError('Failed to load slider data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        // Upload to S3 immediately
        const result = await uploadFile(file, { folderType: 'images' });
        
        setFormData(prev => ({
          ...prev,
          image: file,
          imageUrl: result.file_url,
          s3Key: result.s3_key
        }));
      } catch (error) {
        setError(`Upload failed: ${error.message}`);
      }
    }
  };

  const handleAssetSelect = (asset) => {
    setFormData(prev => ({
      ...prev,
      image: null, // No file object for existing assets
      imageUrl: asset.url,
      s3Key: asset.key
    }));
    setShowAssetBrowser(false);
  };

  const handleAIGenerateImage = async () => {
    if (!aiImageInput.name) {
      setAIImageError('Please provide an image name/title');
      return;
    }

    setGeneratingImage(true);
    setAIImageError('');
    
    try {
      console.log('🎨 Generating AI image for slider:', aiImageInput.name);
      
      const response = await fetch(`${API_BASE_URL}/api/slider-images/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_name: aiImageInput.name,
          image_description: aiImageInput.description,
          model: selectedImageModel
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI image generated:', data.image_url);
        console.log('🔑 S3 key:', data.s3_key);
        
        // Update the image URL and s3_key in the form
        setFormData(prevForm => ({
          ...prevForm,
          imageUrl: data.image_url,
          s3Key: data.s3_key,
          image: null
        }));
        
        // Close modal
        setShowAIImageModal(false);
        setAIImageInput({ name: '', description: '' });
        setAIImageError('');
        
      } else {
        const errorData = await response.json();
        console.error('❌ Error from backend:', errorData);
        setAIImageError(errorData.detail || 'Failed to generate image. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error generating image:', error);
      setAIImageError('Network error. Please check your connection and try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!isEditing && !formData.imageUrl) {
      setError('Please select and upload an image');
      return;
    }

    setSaving(true);
    setShowSavingModal(true);
    setError('');

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        buttonText: formData.buttonText,
        buttonLink: formData.buttonLink,
        order: parseInt(formData.order),
        image_url: formData.imageUrl, // S3 URL
        s3_key: formData.s3Key // For deletion reference
      };

      const url = isEditing 
        ? `${API_BASE_URL}/api/slider-images/${id}`
        : `${API_BASE_URL}/api/slider-images`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Success - wait a moment then redirect back
        setTimeout(() => {
          setShowSavingModal(false);
          navigate('/photo-management?tab=homepageslider');
        }, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save slider image');
      }
    } catch (error) {
      console.error('Error saving slider:', error);
      setError(error.message);
      setShowSavingModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/photo-management?tab=homepageslider');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Slider Image' : 'Add New Slider Image'}
              </h1>
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="Enter slide title"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="Enter slide description"
                />
              </div>

              {/* Button Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="Learn More"
                />
              </div>

              {/* Button Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Link
                </label>
                <input
                  type="url"
                  name="buttonLink"
                  value={formData.buttonLink}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="https://example.com"
                />
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="0"
                  min="0"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isEditing ? 'Replace Image (optional)' : 'Image *'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                  {uploading ? (
                    <div className="space-y-4">
                      <Upload className="h-12 w-12 mx-auto text-theme-blue animate-pulse" />
                      <div>
                        <p className="text-sm text-theme-blue mb-2">Uploading to AWS S3...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-theme-blue h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
                      </div>
                    </div>
                  ) : formData.imageUrl ? (
                    <div className="space-y-4">
                      <div className="relative group">
                        <img 
                          src={formData.imageUrl} 
                          alt="Uploaded" 
                          className="max-h-32 mx-auto rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewImageUrl(formData.imageUrl);
                            setShowImagePreview(true);
                          }}
                          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        >
                          <Eye className="h-8 w-8 text-white" />
                        </button>
                        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-green-600">✅ Image uploaded to AWS S3 successfully</p>
                      <div className="flex items-center justify-center space-x-2">
                        <label className="inline-flex items-center px-3 py-2 text-sm text-theme-blue border border-blue-300 rounded-md hover:bg-blue-50 transition-colors cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" />
                          Replace Image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowAssetBrowser(true)}
                          className="inline-flex items-center px-3 py-2 text-sm text-green-600 border border-green-300 rounded-md hover:bg-green-50 transition-colors"
                        >
                          <Folder className="h-4 w-4 mr-2" />
                          Browse Assets
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAIImageInput({ 
                              name: formData.title || '', 
                              description: formData.description || '' 
                            });
                            setShowAIImageModal(true);
                          }}
                          className="inline-flex items-center px-3 py-2 text-sm text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          AI Generate
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Image className="h-12 w-12 mx-auto text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          {isEditing ? 'Upload a new image to replace the current one' : 'Upload slider image to AWS S3'}
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                          Recommended size: 1920x800px for best results
                        </p>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <label className="inline-flex items-center px-3 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload New
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowAssetBrowser(true)}
                          className="inline-flex items-center px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <Folder className="h-4 w-4 mr-2" />
                          Browse Assets
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAIImageInput({ 
                              name: formData.title || '', 
                              description: formData.description || '' 
                            });
                            setShowAIImageModal(true);
                          }}
                          className="inline-flex items-center px-3 py-2 text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          AI Generate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {formData.image && (
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: {formData.image.name} ({(formData.image.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center px-6 py-2 text-white rounded-md hover:opacity-90 transition-colors disabled:bg-gray-400"
                  style={{ backgroundColor: saving ? '#9CA3AF' : 'var(--theme-primary)' }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : isEditing ? 'Update Slider' : 'Create Slider'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Saving Modal */}
      {showSavingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Saving...
              </h3>
              <p className="text-gray-600">
                {isEditing ? 'Updating slider image' : 'Creating slider image'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* S3 Asset Browser Modal */}
      {showAssetBrowser && (
        <S3AssetBrowser
          isOpen={showAssetBrowser}
          onClose={() => setShowAssetBrowser(false)}
          onSelectAsset={handleAssetSelect}
          allowedTypes={['images']}
          title="Browse Slider Images"
          showUpload={true}
        />
      )}

      {/* AI Generate Image Modal */}
      {showAIImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: 'rgb(147, 51, 234)' }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-white">AI Generate Slider Image</h3>
              </div>
              <button
                onClick={() => {
                  setShowAIImageModal(false);
                  setAIImageInput({ name: '', description: '' });
                  setAIImageError('');
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
                <svg className="w-5 h-5 text-theme-blue mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-800">
                  AI will generate a professional banner image for your website slider based on the title and description. You can edit the details below before generating.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={aiImageInput.name}
                  onChange={(e) => setAIImageInput({ ...aiImageInput, name: e.target.value })}
                  placeholder="Enter slider title (e.g., Welcome to Our Clinic)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={aiImageInput.description}
                  onChange={(e) => setAIImageInput({ ...aiImageInput, description: e.target.value })}
                  placeholder="Optional: Describe the scene or theme you want (e.g., 'Modern veterinary clinic with happy pets and staff')"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Image Model Selection */}
              {availableImageModels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Image Model
                  </label>
                  <select
                    value={selectedImageModel}
                    onChange={(e) => setSelectedImageModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white"
                  >
                    {availableImageModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose which AI model to use for generating the slider image
                  </p>
                </div>
              )}

              {/* Error Message */}
              {aiImageError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-800">{aiImageError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAIImageModal(false);
                    setAIImageInput({ name: '', description: '' });
                    setAIImageError('');
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAIGenerateImage}
                  disabled={generatingImage || !aiImageInput.name}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                >
                  {generatingImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Generate Image
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={() => setShowImagePreview(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={previewImageUrl}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute top-3 right-3 p-2 bg-black bg-opacity-60 rounded hover:bg-opacity-80 transition-all"
              type="button"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SliderImageForm;